import { useDocumentReferenceForBinaryId } from '@ctw/shared/api/fhir/binaries';
import type { BinaryModel } from '@ctw/shared/api/fhir/models/binary';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { RelatedDocumentNarratives } from '@ctw/shared/content/related-documents/related-document-narratives';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { DocumentReferenceDrawerBody } from './document-reference-drawer-action';

export const binaryDrawerAction: UseResourceDrawerOpenDrawerArgs<'Binary'>['action'] = ({
  model,
}) => ({
  label: 'Document',
  title: model.title,
  enableReadActions: true,
  content: {
    body: <BinaryDrawerBody model={model} />,
  },
});

interface BinaryDrawerBodyProps {
  model: BinaryModel;
}

/**
 * A Binary on its own doesn't carry the metadata we want to surface (title, encounter date,
 * author, clinical notes, etc.) — those live on the DocumentReference that points at it. We
 * fetch that DocumentReference directly by binary id and hand the render off to
 * `DocumentReferenceDrawerBody`, which is exactly what `documentReferenceDrawerAction` itself
 * uses.
 */
const BinaryDrawerBody = ({ model }: BinaryDrawerBodyProps) => {
  const { patient } = usePatientContext();

  const documentReferenceQuery = useDocumentReferenceForBinaryId({
    upid: patient.UPID ?? '',
    binaryId: model.id,
    enabled: Boolean(patient.UPID && model.id),
  });

  if (documentReferenceQuery.isLoading) {
    return <LoadingSpinner centered={true} message="Loading..." />;
  }

  const relatedDocument = documentReferenceQuery.data;

  if (!relatedDocument) {
    return (
      <div className={tw`flex flex-col gap-5`}>
        <ContentCard variant="focal" title="Details">
          <DefinitionList items={[{ label: 'Date Retrieved', value: model.lastUpdatedDisplay }]} />
        </ContentCard>
        <RelatedDocumentNarratives key={model.id} binaryId={model.id} />
      </div>
    );
  }

  return <DocumentReferenceDrawerBody model={relatedDocument} />;
};
